// src/components/Services/Services_iOSManagement.jsx
import React from "react";
import { motion } from "framer-motion";
import iosGif from "../images/photos/ios.gif"; // ✅ Ensure correct path

const pageVariants = {
  initial: { rotateY: 90, opacity: 0 },
  in: { rotateY: 0, opacity: 1 },
  out: { rotateY: -90, opacity: 0 },
};

const pageTransition = {
  type: "spring",
  stiffness: 80,
  damping: 20,
};

const fadeUp = {
  hidden: { y: 10, opacity: 0 },
  visible: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.06 * i },
  }),
};

export default function Services_iOSManagement() {
  const services = [
    "Android Zero Touch Enrollment",
    "Android Management",
    "Cloud Computing",
    "Cloud Management Service",
    "Cyber Security",
    "iOS Management",
    "Mobile Management",
    "IT Management",
    "IT Support",
    "Software Development",
    "Digital Marketing",
  ];

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{
        minHeight: "100vh",
        background: "#040d1fff", // ✅ same background
        color: "#e5e9edff",
        padding: "20px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* HERO SECTION */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 32,
            alignItems: "center",
          }}
        >
          {/* LEFT - GIF */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
          >
            <img
              src={iosGif}
              alt="iOS Management"
              style={{
                width: "100%",
                maxWidth: 500,
                height: "auto",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
              loading="lazy"
            />
          </motion.div>

          {/* RIGHT - TEXT */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            style={{
              textAlign: "left",
            }}
          >
            <h1
              style={{
                marginTop: 0,
                fontSize: "2.2rem",
                color: "#6dd4e8ff",
                lineHeight: 1.3,
              }}
            >
              IOS Management
            </h1>
            <p style={{ lineHeight: 1.6, color: "#e2e8f0", fontSize: "1rem" }}>
              Our IOS Management services empower organizations to securely
              deploy, manage, and protect iOS devices at scale. From device
              enrollment to app distribution and compliance enforcement, we
              provide complete solutions for enterprises.
            </p>

            <h3 style={{ marginTop: 16, color: "#53bdd2ff" }}>
              Key IOS Management Services
            </h3>
            <ul style={{ lineHeight: 1.7, color: "#f1f5f9", paddingLeft: 18 }}>
              <li>
                <strong>Device Enrollment & Provisioning</strong> — zero-touch
                setup with Apple Business Manager (ABM).
              </li>
              <li>
                <strong>Security & Compliance</strong> — enforce passcodes,
                encryption, and remote wipe.
              </li>
              <li>
                <strong>App Deployment & Management</strong> — install, update,
                and configure apps securely.
              </li>
              <li>
                <strong>Remote Device Management</strong> — configure and
                troubleshoot remotely.
              </li>
              <li>
                <strong>User & Role Management</strong> — role-based access
                control and AD/LDAP integration.
              </li>
              <li>
                <strong>Content Management</strong> — secure document storage
                and sharing restrictions.
              </li>
              <li>
                <strong>Analytics & Reporting</strong> — monitor compliance,
                usage, and app performance.
              </li>
            </ul>

            {/* Buttons */}
            <div
              style={{
                marginTop: 18,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <a
                href="mailto:support@zyntro.in"
                style={{
                  background: "#f69f48ff",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Email Support
              </a>
              <a
                href="tel:+919956353236"
                style={{
                  border: "1px solid #4e4e54ff",
                  color: "#010101ff",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "white",
                }}
              >
                Call +91-9956353236
              </a>
            </div>
          </motion.div>
        </section>

        {/* OTHER SERVICES */}
        <section style={{ marginTop: 40 }}>
          <h3 style={{ color: "#4fe1f4ff" }}>Other Services</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 14,
              marginTop: 16,
            }}
          >
            {services.map((s) => (
              <div
                key={s}
                style={{
                  background: "#1e293b",
                  padding: 12,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#1e7d95ff",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {s.charAt(0)}
                </div>
                <span style={{ fontSize: 15 }}>{s}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ✅ Responsive Styles */}
      <style>
        {`
          @media (max-width: 1024px) {
            section {
              grid-template-columns: 1fr;
              text-align: center;
            }
            section img {
              margin: 0 auto;
            }
            section div {
              text-align: center;
            }
          }

          @media (max-width: 768px) {
            h1 {
              font-size: 1.8rem !important;
            }
            p, li {
              font-size: 0.95rem !important;
            }
          }

          @media (max-width: 480px) {
            h1 {
              font-size: 1.5rem !important;
            }
            p, li {
              font-size: 0.9rem !important;
            }
            a {
              padding: 8px 12px !important;
              font-size: 0.9rem !important;
            }
          }
        `}
      </style>
    </motion.div>
  );
}
